import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Chip, IconButton, Fade } from '@mui/material';
import {
  AccountBalance, School, People, Calculate, Inventory,
  FamilyRestroom, BarChart, Settings, ChevronLeft, ChevronRight, CheckCircle,
} from '@mui/icons-material';

// Same module copy as the marketing Landing page, kept here so this showcase is
// self-contained and reusable on any onboarding screen (e.g. the inquiry form).
const MODULES = [
  { icon: AccountBalance, color: '#1565c0', label: 'Finance',
    points: ['Fee billing, invoices, receipts & batches', 'GL, journals, banks & reconciliation', 'Balance Sheet, P&L, Trial Balance & budgets', 'Analysis dimensions & VAT / WHT returns'] },
  { icon: School, color: '#2e7d32', label: 'Academics (CBE)',
    points: ['Stages, streams, classes, learning areas', 'Assessment setup, score entry & reports', 'Merit lists & 8-level CBC grading', 'LernAlytics & AI career pathways'] },
  { icon: People, color: '#6a1b9a', label: 'Human Resources',
    points: ['Employees, departments, pay grades', 'Leave management & ledgers', 'Performance Appraisal (goals → PIP)', 'Disciplinary & Exit / clearance'] },
  { icon: Calculate, color: '#c62828', label: 'Payroll',
    points: ['Formula-driven income tax & statutory', 'Statutory levies & custom transactions', 'Payslips & year-end tax forms', 'Periods, runs & statutory reports'] },
  { icon: Inventory, color: '#4e342e', label: 'Supply Chain',
    points: ['Suppliers, purchase orders & LPOs', 'Purchase invoices & payment vouchers', 'Store requisitions & transfers', 'Items, item journals & Fixed Assets'] },
  { icon: FamilyRestroom, color: '#00695c', label: 'Parent Portal',
    points: ["Child's fee balance any time", 'Academic performance per subject', 'Payment confirmations', 'Phone, tablet or computer'] },
  { icon: BarChart, color: '#ef6c00', label: 'Reports & Letterheads',
    points: ['Finance, payroll, HR & academic reports', 'Every report on your letterhead', 'Print / PDF ready for ministries', 'Turnover, met-target, statements'] },
  { icon: Settings, color: '#455a64', label: 'Setups & Security',
    points: ['Company details, logo, tax & bank', 'Number series, currencies, posting', 'Users, groups, permission sets & RBAC', 'Approval workflows & data import'] },
];

/**
 * Auto-sliding showcase of the system's modules and their hot features.
 * Pauses on hover; manual nav (arrows / dots) resets the timer.
 */
export default function FeatureCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = MODULES.length;
  const next = () => setIdx(i => (i + 1) % n);
  const prev = () => setIdx(i => (i - 1 + n) % n);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx(i => (i + 1) % n), 4500);
    return () => clearInterval(t);
  }, [paused, idx, n]);

  const m = MODULES[idx];
  const Icon = m.icon;

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}
    >
      {/* Coloured header bar that shifts with the active module */}
      <Box sx={{
        bgcolor: m.color, transition: 'background-color .5s ease',
        px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Chip label="✨ Explore the system while you wait" size="small"
          sx={{ bgcolor: '#ffffff26', color: '#fff', fontWeight: 700, fontSize: 11 }} />
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={prev} sx={{ color: '#fff', bgcolor: '#ffffff1f', '&:hover': { bgcolor: '#ffffff33' } }}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={next} sx={{ color: '#fff', bgcolor: '#ffffff1f', '&:hover': { bgcolor: '#ffffff33' } }}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Fade in appear key={idx} timeout={450}>
          <Box sx={{ minHeight: 236 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2, flexShrink: 0,
                bgcolor: m.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon sx={{ color: m.color }} />
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary" lineHeight={1.2}>
                  Module {idx + 1} of {n}
                </Typography>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2}>{m.label}</Typography>
              </Box>
            </Box>

            {m.points.map((p, i) => (
              <Box key={i} display="flex" alignItems="flex-start" gap={1} mb={1}>
                <CheckCircle sx={{ fontSize: 18, color: m.color, mt: '2px', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" lineHeight={1.5}>{p}</Typography>
              </Box>
            ))}
          </Box>
        </Fade>

        {/* Progress dots */}
        <Box display="flex" gap={0.8} mt={1} flexWrap="wrap">
          {MODULES.map((mod, i) => (
            <Box key={i} onClick={() => setIdx(i)} sx={{
              width: idx === i ? 20 : 7, height: 7, borderRadius: 4, cursor: 'pointer',
              transition: 'all .3s', bgcolor: idx === i ? m.color : 'action.disabled',
            }} />
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
